import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const ICON = {
    success: <CheckCircle size={15} className="text-emerald-400 shrink-0" />,
    error:   <AlertCircle  size={15} className="text-red-400 shrink-0" />,
    info:    <Info         size={15} className="text-[var(--primary-color)] shrink-0" />,
};

const BORDER = {
    success: 'border-emerald-500/25',
    error:   'border-red-500/25',
    info:    'border-[var(--primary-color)]/25',
};

const GLOW = {
    success: 'shadow-[0_4px_24px_rgba(52,211,153,0.12)]',
    error:   'shadow-[0_4px_24px_rgba(239,68,68,0.12)]',
    info:    'shadow-[0_4px_24px_rgba(24,181,240,0.12)]',
};

/**
 * ToastContainer — renders stacked toast notifications in the bottom-right corner.
 * Reads from AppContext `toasts` array and allows dismissal via `dismissToast`.
 */
const ToastContainer = () => {
    const { toasts, dismissToast } = useApp();

    return (
        <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-2 pointer-events-none">
            <AnimatePresence initial={false}>
                {toasts.map(({ id, message, type = 'success' }) => (
                    <motion.div
                        key={id}
                        layout
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0,  scale: 1    }}
                        exit={{    opacity: 0, y: 10,  scale: 0.95 }}
                        transition={{ type: 'spring', stiffness: 340, damping: 30 }}
                        className={`pointer-events-auto flex items-center gap-3 min-w-[240px] max-w-xs px-4 py-3 rounded-xl border backdrop-blur-xl bg-[rgba(8,10,24,0.9)] text-white text-sm font-medium ${BORDER[type] || BORDER.info} ${GLOW[type] || GLOW.info}`}
                    >
                        {ICON[type] || ICON.info}
                        <span className="flex-1 leading-snug">{message}</span>
                        <button
                            onClick={() => dismissToast(id)}
                            className="shrink-0 text-white/30 hover:text-white/70 transition-colors"
                        >
                            <X size={13} />
                        </button>
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
};

export default ToastContainer;
